import Vue from 'vue'
import Rete, { Socket } from 'rete'
import isFunction from 'lodash.isfunction'
import AreaPlugin from 'rete-area-plugin'
import HistoryPlugin from 'rete-history-plugin'
import VueRenderPlugin from 'rete-vue-render-plugin'
import ConnectionPlugin from 'rete-connection-plugin'
import ContextMenuPlugin from '@unclexo/context-menu-plugin'

import Editor from './NodeEditor'
import Engine from './NodeEngine'

export default class Application {
  static isInitialized
  static editor
  static engine
  static config
  static handlers
  static components
  static isDevMode
  static defaultComponent = null

  constructor(element, configObject, version) {
    Application.isInitialized = true
    this._bootstrap(element, version)  
  }
 
  _bootstrap(element, version) {
    this.element = element
    this.version = version
    this.customPlugins = []

    Application.components = new Map()
    Application.handlers = new Map()
    Application.isDevMode = ! Vue.config.productionTip
    
    Application.editor = Editor.create(version, element)
    Application.engine = Engine.create(version)

    this._resolveConfig()
    this._resolvePlugins()
    this._resolveSockets()
    
    this._registerPlugins()
    this._resolveAndRegisterModules()

    this.defaultComponentName = ''
    this.hasEditableData = false
    this.isDefaultComponentRegistered = false

    // Aborts and proceses data on the following events
    Application.editor.on('process nodecreated noderemoved connectioncreated connectionremoved', 
      () => {
        if(Application.editor.silent) {
          return false
        }
  
        this._compile()     
      }
    )
    
    // Enables resizing and zooming
    Application.editor.view.resize()
    AreaPlugin.zoomAt(Application.editor)
    
    // Triggers 'process' event
    // setTimeout(() => Application.editor.trigger('process'), 1000)    
  }

  _resolveConfig() {
    const config = require('../config/config')

    if (! config.default) {
      throw new Error('Could not find "config" registered for the app.')
    }

    Application.config = config.default
  }

  _resolvePlugins() {
    const plugins = require('../config/plugins')

    if (! plugins.default) {
      throw new Error('Could not find "plugins" registered for the app.')
    }

    for (const plugin of plugins.default) {
      if (plugin.plugin && ! Object.prototype.hasOwnProperty.call(plugin.plugin, 'install')) {
        throw new Error(`The plugin registered with the name "${plugin.name}" has no method named "install"`)
      }
    }
    
    Application.config['plugins'] = plugins.default
  }

  _resolveSockets() {
    const sockets = require('../config/sockets')

    if (! sockets.default) {
      throw new Error('Could not find "sockets" registered for the app.')
    }

    for (const socket of Object.keys(sockets.default)) {
      if (! ((sockets.default[socket]) instanceof Socket)) {
        throw new Error(`The socket registered under "${socket}" property is not an instance of Socket`)
      }
    }
    
    Application.config['sockets'] = sockets.default
  }

  _registerPlugins() {
    this._resolveCustomPlugins()

    const defaultPlugins = this._defaultPlugins()

    const plugins = Application.config.plugins.length > 0
      ? [...defaultPlugins, ...Application.config.plugins]
      : defaultPlugins

    plugins.map(plugin => {
      if (plugin.options && Object.values(plugin.options).length > 0) {                  
        Application.editor.use(plugin.plugin, plugin.options)
      } else {
        Application.editor.use(plugin.plugin)
      }
    })
  }

  _resolveCustomPlugins() {
    const customPlugins = Application.config.plugins
    if (! Array.isArray(customPlugins)) {
      throw new Error("'plugins' property in module config object is not an array")
    }

    customPlugins.map(plugin => {
      const pluginProps = Object.keys(plugin)
      const hasRequiredProperty = pluginProps.indexOf('name') > -1 
        && pluginProps.indexOf('plugin') > -1
      if (! hasRequiredProperty) {
        throw new Error("Invalid custom plugin property provided in module config object")
      }

      const pluginMethod = plugin.plugin ? plugin.plugin.install : null
      const hasInstallMethod = isFunction(pluginMethod)
      if (! hasInstallMethod) {
        throw new Error("Invalid custom plugin provided")
      }      
    })
  }

  _defaultPlugins() {
    return [
      { name: 'area', plugin: AreaPlugin, options: {} },
      { name: 'vueRender', plugin: VueRenderPlugin, options: {} },
      { name: 'connection', plugin: ConnectionPlugin, options: {} },
      { 
        name: 'history', 
        plugin: HistoryPlugin, 
        options: { keyboard: true } 
      },
      { 
        name: 'contextMenu', 
        plugin: ContextMenuPlugin, 
        options: {
          allocate: component => Application
            .config
            .hideFromContextMenu.indexOf(component.name) > -1 
              ? false 
              : []
          ,
          nodeItems: node => {
            if (Application.config.hideCloneMenuFromContextMenu.indexOf(node.name) > -1) {
              return {
                'Clone': false,
              }
            }
    
            return true
          },
        } 
      },
    ]
  }

  _resolveAndRegisterModules() {
    const modules = require('../config/modules')

    if (! modules.default) {
      throw new Error('No modules found to be registered by the app')
    }

    for (const moduleKey of Object.keys(modules.default)) {      
      const module = modules.default[moduleKey]
      const componentPath = moduleKey + '/' + module.component
      const component = require('../app/' + componentPath)

      const componentInstance = new component.default(Application.config)
      
      if (! (componentInstance instanceof Rete.Component)) {
        throw new Error(`Could not resolve component for '${moduleKey}' module`)
      }

      Application.editor.register(componentInstance)
      Application.engine.register(componentInstance)

      Application.components.set(module.name, component.default)
      this._resolveComponentHandlers(module, moduleKey)
    }

    Application.config['modules'] = modules.default
  }

  _resolveComponentHandlers(module, moduleKey) {
    if (module.handlers && Object.values(module.handlers).length > 0) {
      let handlerFunctions = {}
      for (const handlerName of Object.keys(module.handlers)) {
        const handlerFileName = module.handlers[handlerName]
        const handlerFilePath = moduleKey + '/' + handlerFileName
        const handler = require('../app/' + handlerFilePath)

        if (isFunction(handler.default)) {
          handlerFunctions[handlerName] = handler.default 
        } else {
          if (Application.isDevMode) {
            console.log(`'${handlerName}' function not found for '${moduleKey}' module`)
          }
        }
      }

      Application.handlers.set(module.name, handlerFunctions)
      handlerFunctions = {}
    } else {
      if (Application.isDevMode) {
        console.log(`No handlers found for ${moduleKey}`)
      }
    }

    return false
  }

  _compile() {
    Application.engine.abort()
    Application.engine.process(Application.editor.toJSON())
  }

  fromJSON(data) {
    if (this.isDefaultComponentRegistered) {
      throw new Error('Could not create component(s) from JSON data ' + 
      'because the default component has already been set up')
    }

    if (this.hasEditableData) {
      throw new Error('The components have already been created from JSON data')
    }

    this.hasEditableData = true

    Application.editor.fromJSON(data).then(() => {
      Application.editor.view.resize()
      this._compile()
    })
  }

  setDefaultComponent(componentName, position = [0, 0]) {
    if (this.hasEditableData) {
      throw new Error('Could not create default component because the ' + 
      'editable data has already been set up')
    }

    if (this.isDefaultComponentRegistered && this.defaultComponentName) {
      throw new Error(`The '${this.defaultComponentName}' component has already been set up as default`)
    }

    this._setUpDefaultComponent(componentName, position)
  }

  async _setUpDefaultComponent(componentName, position) {
    const component = Application.components.get(componentName)
    if (! component) {
      throw new Error(`Could not set up '${componentName}' component as default`)
    }

    this.defaultComponentName = componentName
    this.isDefaultComponentRegistered = true    

    const componentInstance = new component(Application.config)
    const node = await componentInstance.createNode()
    node.position = position

    Application.editor.addNode(node)
    Application.defaultComponent = node
  }

  static getDefaultComponent() {
    if (this.defaultComponent) {
      return this.defaultComponent
    } else {
      return Application.editor.nodes.length > 0
        ? Application.editor.nodes[0]
        : null
    }
  }

  static async handleNodeData(node, editor, defaultData = null) {
    const moduleHandlers = this.handlers.get(node.name)
    if(moduleHandlers && isFunction(moduleHandlers.dataHandler)) {
      await moduleHandlers.dataHandler(node, editor, defaultData)
    } else {
      if (this.isDevMode) {
        console.log(`Could not call the data handler of '${node.name}' component`)
      }
    }
  }

  static async handleNodeHover(node, editor, element) {
    const moduleHandlers = this.handlers.get(node.name)
    if(moduleHandlers && isFunction(moduleHandlers.hoverHandler)) {
      await moduleHandlers.hoverHandler(node, editor, element)
    } else {
      if (this.isDevMode) {
        console.log(`Could not call the hover handler of '${node.name}' component`)
      }
    }
  }

  static async handleComponentCreate(event, ui) {
    const componentName = ui.draggable[0] 
      ? ui.draggable[0].dataset.type
      : null

    if (componentName) {
      if (this._isUndroppableElement(componentName)) {
        return
      }
  
      await this._createComponent(componentName, event, ui)
    } else {
      throw new Error(`Could not create '${componentName}' component`)
    }
  }

  static _isUndroppableElement(componentName) {
    return Array.isArray(this.config.undroppableElements) 
      && (this.config.undroppableElements.indexOf(componentName) > -1)
  }

  static async _createComponent(componentName, event, ui) {
    const handlers = this.handlers.get(componentName)
    const currentComponent = this.components.get(componentName)

    if (handlers && isFunction(handlers.componentHandler)) {
      await handlers.componentHandler(new currentComponent(this.config), event, ui)
    } else {
      await this.createNode(new currentComponent(this.config), event, ui)
    }
  }

  // eslint-disable-next-line
  static async createNode(component, event, ui) {
    this.editor.view.area.pointermove(event)
    const { x, y } = this.editor.view.area.mouse

    const targetNode = await component.createNode()
    targetNode.position = [x, y]
    
    this.editor.addNode(targetNode)
  }  
}